<script>
  import definition from "./definition";
  import Component from "./component.svelte";
  import Widget from "./widget.svelte";

  // The server injects the page id into a hidden #pageId span via index.tmpl.
  // This is a plain client-mounted Svelte app (not SvelteKit), so reading it
  // from the DOM at mount time is correct.
  const id = /** @type {HTMLElement} */ (document.getElementById("pageId"))
    .textContent;

  // The page's own path, so a link to it can be marked as the page shown.
  const place = { here: document.location.pathname };

  // The rest of a page opening with the banner sits below it, so a header placed at its top is never under the banner.
  /** @param {import("./definition").definition | undefined} def */
  const isBanner = (def) => def?.t === "widget" && def.r?.val === "banner";

  // A screen narrower than this is a phone's, as the copy reads their page in a phone's window.
  const phoneUpTo = 600;

  // The page's parts but its viewport. On a phone's screen a viewport lays the page out as wide as it says, as their page is
  // laid out there, and the phone's browser scales it to the screen.
  /**
   * @param {import("./definition").definition[]} defs
   * @returns {import("./definition").definition[]}
   */
  const laid = (defs) => {
    const width = defs.find((d) => d.t === "viewport")?.p?.width;
    if (width && /^[1-9]\d*$/.test(width) && screen.width < phoneUpTo) {
      document
        .querySelector('meta[name="viewport"]')
        ?.setAttribute("content", `width=${width}`);
    }
    return defs.filter((d) => d.t !== "viewport");
  };

  // A tap on a text-message link is counted as a Pirsch event when Pirsch's script is on the page; the link works either way.
  /** @param {MouseEvent} event */
  const counted = (event) => {
    const pirsch = /** @type {Window & { pirsch?: unknown }} */ (window).pirsch;
    if (typeof pirsch !== "function") return;
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('a[href^="sms:" i]')) return;
    try {
      pirsch("Text link tapped", {
        meta: { page: document.location.pathname },
        non_interactive: false,
      });
    } catch (err) {
      console.debug(err);
    }
  };

  // Whether the page's parts are drawn.
  let drawn = $state(false);

  // A section's name, as the chat opens a preview at #s2 to show the part a change made.
  const sectionName = /^s\d+$/;

  // How long an opened page waits for its section to be drawn before it rings nothing.
  const ringWithin = 5000;

  /** @type {HTMLElement | undefined} */
  let ringed;

  // The section the address names, scrolled into view and ringed as the demo rings a change; true once one is.
  const ringAt = () => {
    const name = document.location.hash.slice(1);
    if (!sectionName.test(name)) return false;
    const part =
      document.getElementById(name) ??
      document.querySelector(`.page > .${name}`);
    if (!(part instanceof HTMLElement)) return false;
    if (ringed && ringed !== part) ringed.style.outline = "";
    ringed = part;
    part.style.outline = "3px solid #0E6F8A";
    part.style.outlineOffset = "-3px";
    part.scrollIntoView?.({ block: "center" });
    return true;
  };

  // Once drawn, a page opened at a section rings it, waiting a while for it to be drawn, and rings again when the
  // address names another.
  $effect(() => {
    if (!drawn) return;
    const seen = new MutationObserver(() => {
      if (ringAt()) seen.disconnect();
    });
    if (!ringAt() && sectionName.test(document.location.hash.slice(1))) {
      seen.observe(document.body, { childList: true, subtree: true });
    }
    const stop = setTimeout(() => seen.disconnect(), ringWithin);
    window.addEventListener("hashchange", ringAt);
    return () => {
      seen.disconnect();
      clearTimeout(stop);
      window.removeEventListener("hashchange", ringAt);
    };
  });

  // The page's parts, marked drawn once they are in.
  /** @param {import("./definition").definition[]} defs */
  const shown = (defs) => {
    drawn = true;
    return defs;
  };

  // The page is drawn after Pirsch binds its own link listeners, so the document listens for the taps.
  $effect(() => {
    document.addEventListener("click", counted, true);
    document.addEventListener("auxclick", counted, true);
    return () => {
      document.removeEventListener("click", counted, true);
      document.removeEventListener("auxclick", counted, true);
    };
  });
</script>

{#await definition.load(id).then(laid).then(shown)}
  loading...
{:then definitions}
  {#if isBanner(definitions[0])}
    <Widget def={definitions[0]} {place} />
    <div class="relative">
      {#each definitions.slice(1) as def}
        {#if def.t === "widget"}
          <Widget {def} {place} />
        {:else}
          <Component {def} {place} />
        {/if}
      {/each}
    </div>
  {:else}
    {#each definitions as def}
      {#if def.t === "widget"}
        <Widget {def} {place} />
      {:else}
        <Component {def} {place} />
      {/if}
    {/each}
  {/if}
{/await}
